import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getChallengesAdmin, getChallengeLeaderboardWeb, getDailyCompletionsDataWeb } from '../services/WebCloudFunctionsService';

interface Challenge {
    id: string;
    title: string;
    description: string;
    category: string;
    level: string;
    metric: string;
    targetValue: number;
    targetUnit: string;
    points: number;
    startDate: string;
    endDate: string;
    isActive: boolean;
    emoji: string;
    type: string;
    organizationId?: string;
}

interface LeaderboardEntry {
    userId: string;
    displayName: string;
    progress: number;
    currentValue: number;
    targetValue: number;
    points: number;
    rank: number;
    lastUpdated: string;
    dailyCompletions?: {
        [date: string]: {
            date: string;
            isCompleted: boolean;
            points: number;
            targetValue: number;
            value: number;
            completedAt?: string | null;
        };
    };
}

// Updated interface to match the new data structure
interface DailyCompletionData {
    date: string;
    formattedDate: string;
    completions: number;
    attempts: number;
    participants: number;
    totalPoints: number;
    totalAttemptPoints: number;
    averagePoints: number;
    averageAttemptPoints: number;
    completionDetails: Array<{
        userId: string;
        displayName: string;
        challengeId: string;
        challengeName: string;
        challengeEmoji: string;
        completedAt: string;
        points: number;
        value: number;
        targetValue: number;
        isCompleted: boolean;
    }>;
    attemptDetails: Array<{
        userId: string;
        displayName: string;
        challengeId: string;
        challengeName: string;
        challengeEmoji: string;
        completedAt: string;
        points: number;
        value: number;
        targetValue: number;
        isCompleted: boolean;
    }>;
}

const Leaderboards: React.FC = () => {
    const { user } = useAuth();
    const [challenges, setChallenges] = useState<Challenge[]>([]);
    const [selectedChallenge, setSelectedChallenge] = useState<string>('');
    const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
    const [dailyCompletions, setDailyCompletions] = useState<DailyCompletionData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [startDate, setStartDate] = useState<string>(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [viewMode, setViewMode] = useState<'leaderboard' | 'daily'>('leaderboard');

    useEffect(() => {
        loadChallenges();
    }, []);

    useEffect(() => {
        if (selectedChallenge && viewMode === 'leaderboard') {
            loadLeaderboard();
        }
    }, [selectedChallenge, viewMode]);

    useEffect(() => {
        if (viewMode === 'daily') {
            loadDailyCompletions();
        }
    }, [viewMode]); // Only trigger on view mode change, not date changes

    const loadChallenges = async () => {
        try {
            setIsLoading(true);
            const challengesData = await getChallengesAdmin();
            // Handle the new return type structure
            if (challengesData && challengesData.challenges && Array.isArray(challengesData.challenges)) {
                setChallenges(challengesData.challenges);
                if (challengesData.challenges.length > 0) {
                    setSelectedChallenge(challengesData.challenges[0].id);
                }
            } else {
                setChallenges([]);
            }
        } catch (error) {
            console.error('Failed to load challenges:', error);
            setChallenges([]);
        } finally {
            setIsLoading(false);
        }
    };

    const loadLeaderboard = async () => {
        if (!selectedChallenge) return;

        try {
            setIsLoading(true);
            const leaderboard = await getChallengeLeaderboardWeb(selectedChallenge);
            if (leaderboard && leaderboard.entries) {
                setLeaderboardData(leaderboard.entries);
            } else {
                setLeaderboardData([]);
            }
        } catch (error) {
            console.error('Failed to load leaderboard:', error);
            setLeaderboardData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const loadDailyCompletions = async () => {
        try {
            console.log('Loading daily completions...');
            const dailyDataResponse = await getDailyCompletionsDataWeb({
                timeRange: '30d',
                startDate,
                endDate
            });
            console.log('Daily completions response:', dailyDataResponse);

            if (dailyDataResponse && dailyDataResponse.dailyData && dailyDataResponse.dailyData.length > 0) {
                console.log('Setting daily completions data:', dailyDataResponse.dailyData.length, 'days');
                console.log('First day data:', dailyDataResponse.dailyData[0]);
                setDailyCompletions(dailyDataResponse.dailyData);
                setSelectedDate(dailyDataResponse.dailyData[0].date);
                console.log('State updated - dailyCompletions length:', dailyDataResponse.dailyData.length);
            } else {
                // Fallback to mock data
                const mockData: DailyCompletionData[] = [
                    {
                        date: new Date().toISOString().split('T')[0],
                        formattedDate: new Date().toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                        }),
                        completions: 156,
                        attempts: 234,
                        participants: 234,
                        totalPoints: 12345,
                        totalAttemptPoints: 15678,
                        averagePoints: 52.7,
                        averageAttemptPoints: 67.0,
                        completionDetails: [
                            { userId: '1', displayName: 'Sarah Johnson', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '09:30', points: 50, value: 100, targetValue: 100, isCompleted: true },
                            { userId: '2', displayName: 'Mike Chen', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '👥', completedAt: '08:45', points: 47, value: 95, targetValue: 100, isCompleted: true },
                            { userId: '3', displayName: 'Emma Davis', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '📊', completedAt: '10:15', points: 46, value: 92, targetValue: 100, isCompleted: true },
                        ],
                        attemptDetails: [
                            { userId: '1', displayName: 'Sarah Johnson', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '09:30', points: 50, value: 100, targetValue: 100, isCompleted: true },
                            { userId: '2', displayName: 'Mike Chen', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '👥', completedAt: '08:45', points: 47, value: 95, targetValue: 100, isCompleted: true },
                            { userId: '3', displayName: 'Emma Davis', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '📊', completedAt: '10:15', points: 46, value: 92, targetValue: 100, isCompleted: true },
                            { userId: '4', displayName: 'John Doe', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '11:20', points: 25, value: 50, targetValue: 100, isCompleted: false },
                            { userId: '5', displayName: 'Jane Smith', challengeId: '3', challengeName: 'Challenge C', challengeEmoji: '💪', completedAt: '12:00', points: 30, value: 60, targetValue: 100, isCompleted: false },
                        ]
                    },
                    {
                        date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        formattedDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                        }),
                        completions: 142,
                        attempts: 198,
                        participants: 234,
                        totalPoints: 11234,
                        totalAttemptPoints: 14234,
                        averagePoints: 48.0,
                        averageAttemptPoints: 71.9,
                        completionDetails: [
                            { userId: '4', displayName: 'Alex Rodriguez', challengeId: '3', challengeName: 'Challenge C', challengeEmoji: '🏆', completedAt: '09:15', points: 49, value: 98, targetValue: 100, isCompleted: true },
                            { userId: '5', displayName: 'Lisa Wang', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '👥', completedAt: '08:30', points: 47, value: 94, targetValue: 100, isCompleted: true },
                            { userId: '1', displayName: 'Sarah Johnson', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '📊', completedAt: '10:00', points: 45, value: 91, targetValue: 100, isCompleted: true },
                        ],
                        attemptDetails: [
                            { userId: '4', displayName: 'Alex Rodriguez', challengeId: '3', challengeName: 'Challenge C', challengeEmoji: '🏆', completedAt: '09:15', points: 49, value: 98, targetValue: 100, isCompleted: true },
                            { userId: '5', displayName: 'Lisa Wang', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '👥', completedAt: '08:30', points: 47, value: 94, targetValue: 100, isCompleted: true },
                            { userId: '1', displayName: 'Sarah Johnson', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '📊', completedAt: '10:00', points: 45, value: 91, targetValue: 100, isCompleted: true },
                            { userId: '7', displayName: 'Tom Wilson', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '11:45', points: 20, value: 40, targetValue: 100, isCompleted: false },
                            { userId: '8', displayName: 'Anna Brown', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '👥', completedAt: '12:30', points: 35, value: 70, targetValue: 100, isCompleted: false },
                        ]
                    },
                    {
                        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        formattedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                        }),
                        completions: 138,
                        attempts: 167,
                        participants: 234,
                        totalPoints: 10123,
                        totalAttemptPoints: 12345,
                        averagePoints: 43.3,
                        averageAttemptPoints: 73.9,
                        completionDetails: [
                            { userId: '2', displayName: 'Mike Chen', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '🏆', completedAt: '09:00', points: 48, value: 97, targetValue: 100, isCompleted: true },
                            { userId: '3', displayName: 'Emma Davis', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '👥', completedAt: '08:45', points: 46, value: 93, targetValue: 100, isCompleted: true },
                            { userId: '6', displayName: 'David Kim', challengeId: '3', challengeName: 'Challenge C', challengeEmoji: '📊', completedAt: '10:30', points: 44, value: 89, targetValue: 100, isCompleted: true },
                        ],
                        attemptDetails: [
                            { userId: '2', displayName: 'Mike Chen', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '🏆', completedAt: '09:00', points: 48, value: 97, targetValue: 100, isCompleted: true },
                            { userId: '3', displayName: 'Emma Davis', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '👥', completedAt: '08:45', points: 46, value: 93, targetValue: 100, isCompleted: true },
                            { userId: '6', displayName: 'David Kim', challengeId: '3', challengeName: 'Challenge C', challengeEmoji: '📊', completedAt: '10:30', points: 44, value: 89, targetValue: 100, isCompleted: true },
                            { userId: '9', displayName: 'Chris Lee', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '13:15', points: 15, value: 30, targetValue: 100, isCompleted: false },
                            { userId: '10', displayName: 'Maria Garcia', challengeId: '3', challengeName: 'Challenge C', challengeEmoji: '💪', completedAt: '14:00', points: 28, value: 56, targetValue: 100, isCompleted: false },
                        ]
                    }
                ];
                setDailyCompletions(mockData);
                setSelectedDate(mockData[0].date);
            }
        } catch (error) {
            console.error('Failed to load daily completions:', error);
            console.error('Error details:', error);
            // Fallback to mock data
            const mockData: DailyCompletionData[] = [
                {
                    date: new Date().toISOString().split('T')[0],
                    formattedDate: new Date().toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                    }),
                    completions: 156,
                    attempts: 234,
                    participants: 234,
                    totalPoints: 12345,
                    totalAttemptPoints: 15678,
                    averagePoints: 52.7,
                    averageAttemptPoints: 67.0,
                    completionDetails: [
                        { userId: '1', displayName: 'Sarah Johnson', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '09:30', points: 50, value: 100, targetValue: 100, isCompleted: true },
                        { userId: '2', displayName: 'Mike Chen', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '👥', completedAt: '08:45', points: 47, value: 95, targetValue: 100, isCompleted: true },
                        { userId: '3', displayName: 'Emma Davis', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '📊', completedAt: '10:15', points: 46, value: 92, targetValue: 100, isCompleted: true },
                    ],
                    attemptDetails: [
                        { userId: '1', displayName: 'Sarah Johnson', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '09:30', points: 50, value: 100, targetValue: 100, isCompleted: true },
                        { userId: '2', displayName: 'Mike Chen', challengeId: '2', challengeName: 'Challenge B', challengeEmoji: '👥', completedAt: '08:45', points: 47, value: 95, targetValue: 100, isCompleted: true },
                        { userId: '3', displayName: 'Emma Davis', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '📊', completedAt: '10:15', points: 46, value: 92, targetValue: 100, isCompleted: true },
                        { userId: '4', displayName: 'John Doe', challengeId: '1', challengeName: 'Challenge A', challengeEmoji: '🏆', completedAt: '11:20', points: 25, value: 50, targetValue: 100, isCompleted: false },
                        { userId: '5', displayName: 'Jane Smith', challengeId: '3', challengeName: 'Challenge C', challengeEmoji: '💪', completedAt: '12:00', points: 30, value: 60, targetValue: 100, isCompleted: false },
                    ]
                }
            ];
            setDailyCompletions(mockData);
            setSelectedDate(mockData[0].date);
        }
    };

    const getProgressColor = (progress: number) => {
        if (progress >= 80) return 'text-green-500';
        if (progress >= 60) return 'text-yellow-500';
        return 'text-red-500';
    };

    const getProgressBarColor = (progress: number) => {
        if (progress >= 80) return 'bg-green-500';
        if (progress >= 60) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const getRankEmoji = (rank: number) => {
        if (rank === 1) return '🥇';
        if (rank === 2) return '🥈';
        if (rank === 3) return '🥉';
        return `#${rank}`;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900/20 dark:to-indigo-900/20 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading leaderboards...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900/20 dark:to-indigo-900/20 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3">
                        🏆 Leaderboards & Daily Completions
                    </h1>
                    <p className="text-gray-600 dark:text-gray-300 text-base">
                        Monitor challenge performance and daily user engagement
                    </p>
                </div>

                {/* View Mode Toggle */}
                <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6 mb-6">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => setViewMode('leaderboard')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${viewMode === 'leaderboard'
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                        >
                            🏆 Challenge Leaderboards
                        </button>
                        <button
                            onClick={() => setViewMode('daily')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${viewMode === 'daily'
                                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                        >
                            📅 Daily Completions
                        </button>
                    </div>
                </div>

                {viewMode === 'leaderboard' ? (
                    /* Challenge Leaderboards View */
                    <div className="space-y-6">
                        {/* Challenge Selection */}
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                🎯 Select Challenge
                            </label>
                            <select
                                value={selectedChallenge}
                                onChange={(e) => setSelectedChallenge(e.target.value)}
                                className="w-full px-4 py-3 border-0 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 text-gray-900 dark:text-white focus:ring-4 focus:ring-blue-500/30 focus:outline-none shadow-inner transition-all duration-300"
                            >
                                <option value="">Select a challenge...</option>
                                {challenges.map((challenge) => (
                                    <option key={challenge.id} value={challenge.id}>
                                        {challenge.emoji} {challenge.title}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Leaderboard */}
                        {selectedChallenge && leaderboardData.length > 0 && (
                            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:border-gray-700/50 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    🏆 Challenge Leaderboard
                                </h3>
                                <div className="space-y-3">
                                    {leaderboardData.map((entry, index) => (
                                        <div key={entry.userId} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                                    {getRankEmoji(entry.rank)}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">{entry.displayName}</p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {entry.currentValue} / {entry.targetValue} {challenges.find(c => c.id === selectedChallenge)?.targetUnit}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-6">
                                                <div className="text-right">
                                                    <p className={`font-semibold ${getProgressColor(entry.progress)}`}>
                                                        {entry.progress}%
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Progress</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-purple-600 dark:text-purple-400">
                                                        {entry.points}
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Points</p>
                                                </div>
                                                <div className="w-32">
                                                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                                        <div
                                                            className={`h-2 rounded-full ${getProgressBarColor(entry.progress)}`}
                                                            style={{ width: `${entry.progress}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedChallenge && leaderboardData.length === 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center">
                                <p className="text-gray-500 dark:text-gray-400">No leaderboard data available for this challenge.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Daily Completions View */
                    <div className="space-y-6">
                        {/* Date Range Selection */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                                Date Range
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <button
                                        onClick={loadDailyCompletions}
                                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                    >
                                        Search
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Date Selection for Details */}
                        {/* <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Date to View Details
                            </label>
                            <select
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {dailyCompletions.map((day) => (
                                    <option key={day.date} value={day.date}>
                                        {day.formattedDate}
                                    </option>
                                ))}
                            </select>
                        </div> */}

                        {/* Debug Info */}
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">Debug Info:</h4>
                            <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                Daily Completions Length: {dailyCompletions.length}
                            </p>
                            <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                Selected Date: {selectedDate}
                            </p>
                            <p className="text-xs text-yellow-700 dark:text-yellow-300">
                                Selected Date Data: {dailyCompletions.find(d => d.date === selectedDate) ? 'Found' : 'Not Found'}
                            </p>
                        </div>

                        {/* Daily Summary */}
                        {selectedDate && (
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                                            <span className="text-2xl">✅</span>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Completions</p>
                                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                {dailyCompletions.find(d => d.date === selectedDate)?.completions || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                                            <span className="text-2xl">👥</span>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Participants</p>
                                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                {dailyCompletions.find(d => d.date === selectedDate)?.participants || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                                            <span className="text-2xl">📊</span>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Points</p>
                                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                {dailyCompletions.find(d => d.date === selectedDate)?.averagePoints || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                                            <span className="text-2xl">⭐</span>
                                        </div>
                                        <div className="ml-4">
                                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Points</p>
                                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                {dailyCompletions.find(d => d.date === selectedDate)?.totalPoints || 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Top Users for Selected Date */}
                        {selectedDate && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    🏆 Completed Challenges - {dailyCompletions.find(d => d.date === selectedDate)?.formattedDate}
                                </h3>
                                <div className="space-y-3 mb-6">
                                    {dailyCompletions.find(d => d.date === selectedDate)?.completionDetails.map((user, index) => (
                                        <div key={`completed-${user.userId}-${user.challengeId}`} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 dark:text-white">{user.displayName}</p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {user.challengeEmoji} {user.challengeName}
                                                    </p>
                                                    <p className="text-xs text-gray-400 dark:text-gray-500">
                                                        Completed at {user.completedAt}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-6">
                                                <div className="text-right">
                                                    <p className={`font-semibold ${getProgressColor((user.value / user.targetValue) * 100)}`}>
                                                        {Math.round((user.value / user.targetValue) * 100)}%
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Progress</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-purple-600 dark:text-purple-400">
                                                        {user.points}
                                                    </p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Points</p>
                                                </div>
                                            </div>
                                        </div>
                                    )) || []}
                                </div>

                                {/* Show attempts (non-completed activities) */}
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    🎯 Activity Attempts - {dailyCompletions.find(d => d.date === selectedDate)?.formattedDate}
                                </h3>
                                <div className="space-y-3">
                                    {dailyCompletions.find(d => d.date === selectedDate)?.attemptDetails
                                        ?.filter(attempt => !attempt.isCompleted)
                                        ?.map((user, index) => (
                                            <div key={`attempt-${user.userId}-${user.challengeId}`} className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                                <div className="flex items-center space-x-4">
                                                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                                        {index + 1}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">{user.displayName}</p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            {user.challengeEmoji} {user.challengeName}
                                                        </p>
                                                        <p className="text-xs text-gray-400 dark:text-gray-500">
                                                            Attempted at {user.completedAt}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-6">
                                                    <div className="text-right">
                                                        <p className={`font-semibold ${getProgressColor((user.value / user.targetValue) * 100)}`}>
                                                            {Math.round((user.value / user.targetValue) * 100)}%
                                                        </p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">Progress</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="font-semibold text-orange-600 dark:text-orange-400">
                                                            {user.points}
                                                        </p>
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">Partial Points</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )) || []}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Leaderboards;
